// ============================================
// RADAR VIEW - Spider Chart Component
// ============================================

import {
    Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    ResponsiveContainer, Legend, Tooltip
} from 'recharts';
import type { Employee, CategoryStats } from '../../types/results';
import { getHebrewName } from '../../constants/muscles';
import { DEMOGRAPHIC_COLORS, DEMOGRAPHIC_LABELS } from '../../constants/demographics';

interface RadarViewProps {
    employee: Employee;
    groupAvg: CategoryStats;
    globalAvg?: CategoryStats;  // Dynamic global averages from SQL
    hideTeam?: boolean;
    ageGroupAvg?: CategoryStats | null;
    genderGroupAvg?: CategoryStats | null;
}

export default function RadarView({ employee, groupAvg, globalAvg, hideTeam, ageGroupAvg, genderGroupAvg }: RadarViewProps) {
    // Get all categories from the employee scores
    const categories = Object.keys(employee.scores);

    const data = categories.map(category => ({
        subject: getHebrewName(category),
        personal: employee.scores[category] ?? 0,
        group: groupAvg[category] ?? 0,
        global: globalAvg?.[category] ?? 6.0,
        ...(ageGroupAvg ? { ageGroup: ageGroupAvg[category] ?? 0 } : {}),
        ...(genderGroupAvg ? { genderGroup: genderGroupAvg[category] ?? 0 } : {}),
        fullMark: 10,
    }));

    return (
        <div className="analytics-radar-container" style={{
            direction: 'ltr',
            margin: '0 20px',
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}>
                    <PolarGrid />
                    <PolarAngleAxis
                        dataKey="subject"
                        tick={{ fontSize: 10, fill: '#756d5e' }}
                    />
                    <PolarRadiusAxis
                        angle={30}
                        domain={[1, 10]}
                        ticks={[2, 4, 6, 8, 10] as any}
                        axisLine={{ stroke: '#ebe6de', strokeDasharray: '4 4' }}
                        tick={{ fontSize: 10, fill: '#938977', fontWeight: 600 }}
                    />
                    <Radar
                        name="ממוצע גלובלי"
                        dataKey="global"
                        stroke="#c17d5f"
                        fill="#c17d5f"
                        fillOpacity={0.05}
                    />
                    {ageGroupAvg && (
                        <Radar
                            name={DEMOGRAPHIC_LABELS.ageGroup}
                            dataKey="ageGroup"
                            stroke={DEMOGRAPHIC_COLORS.ageGroup}
                            fill={DEMOGRAPHIC_COLORS.ageGroup}
                            fillOpacity={0.05}
                            strokeDasharray="5 5"
                        />
                    )}
                    {genderGroupAvg && (
                        <Radar
                            name={DEMOGRAPHIC_LABELS.genderGroup}
                            dataKey="genderGroup"
                            stroke={DEMOGRAPHIC_COLORS.genderGroup}
                            fill={DEMOGRAPHIC_COLORS.genderGroup}
                            fillOpacity={0.05}
                            strokeDasharray="5 5"
                        />
                    )}
                    {!hideTeam && (
                        <Radar
                            name="ממוצע קבוצה"
                            dataKey="group"
                            stroke="#5e7d94"
                            fill="#5e7d94"
                            fillOpacity={0.1}
                        />
                    )}
                    <Radar
                        name={employee.name}
                        dataKey="personal"
                        stroke="#133437"
                        fill="#133437"
                        fillOpacity={0.4}
                    />
                    <Legend />
                    <Tooltip
                        formatter={(value: any, name?: string) => [
                            typeof value === 'number' ? value.toFixed(2) : value,
                            name || ''
                        ]}
                        contentStyle={{
                            borderRadius: '8px',
                            border: 'none',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                            direction: 'rtl',
                            textAlign: 'right'
                        }}
                    />
                </RadarChart>
            </ResponsiveContainer>
        </div>
    );
}
